import type { Supplier } from "@/apis/gql/types";
import { IbanInput } from "@/components/shared/form/iban-input";
import { TextAreaInput } from "@/components/shared/form/text-area-input";
import { TextInput } from "@/components/shared/form/text-input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/react";
import i18next from "i18next";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";

const formSchema = z.object({
  supplierName: z.string().min(1, i18next.t("name_required")),
  datevAccountNumber: z.string().trim(),
  isCategory: z.boolean(),
  IBAN: z.string().optional().nullable(),
  customerNumber: z.string().optional().nullable(),
  promptText: z.string().optional().nullable(),
});

type FormValues = z.infer<typeof formSchema>;

export const CounterpartyEditForm = ({
  type,
  supplier,
  onUpdate,
}: {
  type: "creditor" | "debtor";
  supplier: Supplier;
  onUpdate: (supplierId: string, updatedFields: any) => Promise<void>;
}) => {
  const { t } = useTranslation();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      supplierName: supplier.supplierName || "",
      datevAccountNumber: supplier.datevAccountNumber || "",
      isCategory: supplier.isCategory || false,
      IBAN: supplier.paymentDetails?.IBAN || "",
      customerNumber: supplier.customerNumber || "",
      promptText: supplier.promptText || "",
    },
  });

  const onSubmit = async <K extends keyof FormValues>(
    field: K,
    value: FormValues[K],
  ) => {
    try {
      const isValid = await form.trigger(field);

      if (!isValid) return;

      const updateValues =
        field === "IBAN"
          ? { paymentDetails: { IBAN: value } }
          : { [field]: value };

      await onUpdate(supplier.supplierId!, updateValues);
    } catch (error) {
      Sentry.captureException(error);
      console.error(`Error saving ${field}:`, error);
    }
  };

  const datevAccountNumberLabel = useMemo(() => {
    const key = (
      {
        creditor: "creditors.datev_account_number",
        debtor: "debtors.datev_account_number",
      } as const
    )[type];
    return t(key);
  }, [type]);

  return (
    <div className="mt-2">
      <Form {...form}>
        <div className="flex gap-2 flex-col">
          <TextInput
            name="supplierName"
            label={t("name")}
            placeholder={t("name")}
            onBlur={(e) =>
              onSubmit("supplierName", (e.target as HTMLInputElement).value)
            }
          />
          <TextInput
            name="datevAccountNumber"
            label={datevAccountNumberLabel}
            placeholder={datevAccountNumberLabel}
            onBlur={(e) =>
              onSubmit(
                "datevAccountNumber",
                (e.target as HTMLInputElement).value,
              )
            }
          />
          <IbanInput
            name="IBAN"
            label={t("IBAN")}
            placeholder={t("IBAN")}
            onBlur={(e) => onSubmit("IBAN", e.target.value)}
          />
          {type === "debtor" && (
            <TextInput
              name="customerNumber"
              label={t("customer_number")}
              placeholder={t("customer_number")}
              onBlur={(e) => onSubmit("customerNumber", e.target.value)}
            />
          )}
          <FormField
            control={form.control}
            name="isCategory"
            render={({ field }) => (
              <FormItem className="mt-6">
                <FormLabel>
                  {
                    {
                      creditor: t("creditors.group"),
                      debtor: t("debtors.group"),
                    }[type]
                  }
                </FormLabel>
                <label
                  htmlFor="isCategory"
                  className={`flex items-start space-x-3 rounded-md border p-4 cursor-pointer hover:bg-accent/50 transition-colors ${
                    field.value
                      ? "border-blue-600 bg-blue-50 dark:border-blue-900 dark:bg-blue-950"
                      : "border-input"
                  }`}
                >
                  <FormControl>
                    <Checkbox
                      id="isCategory"
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        onSubmit("isCategory", Boolean(checked));
                      }}
                      className="data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white dark:data-[state=checked]:border-blue-700 dark:data-[state=checked]:bg-blue-700"
                    />
                  </FormControl>
                  <div className="flex-1 space-y-1">
                    <div className="text-sm font-medium leading-none">
                      {
                        {
                          creditor: t("creditors.group_checkbox_label"),
                          debtor: t("debtors.group_checkbox_label"),
                        }[type]
                      }
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {
                        {
                          creditor: t("creditors.group_hint"),
                          debtor: t("debtors.group_hint"),
                        }[type]
                      }
                    </p>
                  </div>
                </label>
              </FormItem>
            )}
          />
          {type === "debtor" && form.watch("isCategory") && (
            <div className="space-y-1">
              <TextAreaInput
                name="promptText"
                label={t("debtors.prompt_guidance_label")}
                placeholder={t("debtors.prompt_guidance_placeholder")}
                onBlur={(e) => onSubmit("promptText", e.target.value)}
                className="focus-visible:ring-0 focus-visible:outline-muted-foreground focus-visible:outline-offset-0"
              />
              <p className="text-sm text-muted-foreground">
                {t("debtors.prompt_guidance_hint")}
              </p>
            </div>
          )}
        </div>
      </Form>
    </div>
  );
};
